import React, { useState, useEffect, useCallback } from 'react';
import { Eye, RefreshCw, MessageCircle, CheckCircle, AlertCircle } from 'lucide-react';
import { SlackWizardConfig } from '../SlackWizard';
import { useSlackPreview } from '../hooks/useSlackPreview';

interface PreviewStepProps {
  nodeId: string;
  config: SlackWizardConfig;
  onChange: (updates: Partial<SlackWizardConfig>) => void;
  errors: string[];
}

export function PreviewStep({
  nodeId,
  config,
  onChange,
  errors,
}: PreviewStepProps) {
  const [sampleData, setSampleData] = useState({
    user: {
      id: 'user_123',
      name: 'John Doe',
      email: 'john.doe@example.com',
    },
    order: {
      id: 'ORD-12345',
      total: 99.99,
      currency: 'USD',
      status: 'completed',
      url: 'https://example.com/orders/12345',
    },
    customer: {
      name: 'Jane Smith',
      email: 'jane.smith@example.com',
    },
    execution: {
      id: 'exec_abc123',
      startTime: new Date().toISOString(),
      currentStep: 3,
    },
  });

  const [customSampleData, setCustomSampleData] = useState('');
  const [useCustomData, setUseCustomData] = useState(false);
  const [jsonError, setJsonError] = useState<string | null>(null);

  const { preview, isLoading, error, generatePreview } = useSlackPreview();

  // Generate preview when config or sample data changes
  useEffect(() => {
    if (config.credentialId && config.channels.length > 0 && config.content) {
      const contextData = useCustomData ? parseCustomData() : sampleData;
      if (contextData) {
        generatePreview(config, contextData);
      }
    }
  }, [config, sampleData, useCustomData, customSampleData, generatePreview]);

  const parseCustomData = useCallback(() => {
    if (!customSampleData.trim()) return sampleData;
    
    try {
      const parsed = JSON.parse(customSampleData);
      setJsonError(null);
      return parsed;
    } catch (error) {
      setJsonError(error instanceof Error ? error.message : 'Invalid JSON');
      return null;
    }
  }, [customSampleData, sampleData]);

  const handleCustomDataChange = useCallback((value: string) => {
    setCustomSampleData(value);
  }, []);

  const handleRefreshPreview = useCallback(() => {
    const contextData = useCustomData ? parseCustomData() : sampleData;
    if (contextData) {
      generatePreview(config, contextData);
    }
  }, [config, sampleData, useCustomData, parseCustomData, generatePreview]);

  const renderPreviewContent = (renderedContent: any, format: string) => {
    switch (format) {
      case 'text':
        return (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded p-3">
            <div className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
              {renderedContent.text}
            </div>
          </div>
        );
      
      case 'markdown':
        return (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded p-3">
            <div className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap font-mono">
              {renderedContent.text}
            </div>
            <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Slack will render markdown formatting (*bold*, _italic_, etc.)
              </div>
            </div>
          </div>
        );
      
      case 'blocks':
        return (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded p-3">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Block Kit JSON Preview:
            </div>
            <pre className="text-xs text-gray-800 dark:text-gray-200 whitespace-pre-wrap font-mono bg-gray-50 dark:bg-gray-700 p-2 rounded border">
              {JSON.stringify(renderedContent.blocks, null, 2)}
            </pre>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Preview Your Message
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          See how your message will look with sample data and validate the configuration.
        </p>
      </div>

      {/* Sample Data Configuration */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Sample Data for Preview
        </label>
        
        <div className="space-y-3">
          <div className="flex items-center">
            <input
              type="radio"
              id="default-sample"
              name="sample-data"
              checked={!useCustomData}
              onChange={() => setUseCustomData(false)}
              className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600"
            />
            <label htmlFor="default-sample" className="ml-3 block text-sm text-gray-900 dark:text-white">
              Use default sample data
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="radio"
              id="custom-sample"
              name="sample-data"
              checked={useCustomData}
              onChange={() => setUseCustomData(true)}
              className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600"
            />
            <label htmlFor="custom-sample" className="ml-3 block text-sm text-gray-900 dark:text-white">
              Provide custom sample data (JSON)
            </label>
          </div>
        </div>

        {!useCustomData && (
          <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded p-3">
            <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">Default Sample Data:</div>
            <pre className="text-xs text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
              {JSON.stringify(sampleData, null, 2)}
            </pre>
          </div>
        )}

        {useCustomData && (
          <div className="space-y-2">
            <textarea
              value={customSampleData}
              onChange={(e) => handleCustomDataChange(e.target.value)}
              placeholder={JSON.stringify(sampleData, null, 2)}
              className={`block w-full px-3 py-2 border rounded-md shadow-sm font-mono text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 min-h-32 ${
                jsonError
                  ? 'border-red-300 dark:border-red-600 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500'
              } bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
            />
            {jsonError && (
              <p className="text-sm text-red-600 dark:text-red-400">
                JSON Error: {jsonError}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Preview Controls */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Message Preview
        </h4>
        <button
          type="button"
          onClick={handleRefreshPreview}
          disabled={isLoading || (useCustomData && !!jsonError)}
          className="inline-flex items-center px-3 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw className={`w-3 h-3 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Preview Results */}
      <div className="space-y-4">
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-5 h-5 animate-spin text-gray-400 mr-2" />
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Generating preview...
            </span>
          </div>
        )}

        {error && (
          <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                  Preview Error
                </h3>
                <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                  {error}
                </div>
              </div>
            </div>
          </div>
        )}

        {preview && !isLoading && (
          <div className="space-y-4">
            {/* Summary */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-md p-4">
              <div className="flex items-center">
                <Eye className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100">
                    Preview Summary
                  </h4>
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    {preview.summary.validChannels} valid, {preview.summary.invalidChannels} invalid • {preview.summary.tokensResolved} tokens resolved
                  </p>
                </div>
              </div>
            </div>

            {/* Channel Previews */}
            {preview.channels.map((channel, index) => (
              <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-md">
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {channel.channelName || channel.channel}
                      </span>
                      {channel.valid ? (
                        <CheckCircle className="w-4 h-4 text-green-500 ml-2" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-red-500 ml-2" />
                      )}
                    </div>
                    <span className={`text-xs px-2 py-1 rounded ${
                      channel.valid 
                        ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200'
                        : 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-200'
                    }`}>
                      {channel.valid ? 'Valid' : 'Invalid'}
                    </span>
                  </div>
                </div>
                
                <div className="p-4">
                  {channel.valid ? (
                    renderPreviewContent(channel.renderedContent, channel.renderedContent.format)
                  ) : (
                    <div className="text-sm text-red-600 dark:text-red-400">
                      <div className="font-medium mb-1">Errors:</div>
                      <ul className="list-disc list-inside space-y-1">
                        {channel.errors?.map((error, errorIndex) => (
                          <li key={errorIndex}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {channel.warnings && channel.warnings.length > 0 && (
                    <div className="mt-3 text-sm text-yellow-600 dark:text-yellow-400">
                      <div className="font-medium mb-1">Warnings:</div>
                      <ul className="list-disc list-inside space-y-1">
                        {channel.warnings.map((warning, warningIndex) => (
                          <li key={warningIndex}>{warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Tokens Found */}
            {preview.summary.tokensFound.length > 0 && (
              <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md p-4">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Variables Found
                </h4>
                <div className="flex flex-wrap gap-1">
                  {preview.summary.tokensFound.map((token, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-800 dark:text-blue-200 bg-blue-100 dark:bg-blue-900/20 rounded"
                    >
                      {'{{' + token + '}}'}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Configuration Errors */}
      {errors.length > 0 && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
          <div className="flex">
            <MessageCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                Configuration Issues
              </h3>
              <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                <ul className="list-disc list-inside space-y-1">
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
